/**
 * SettingsDialog — per-user display preferences (Issue #56).
 *
 * Hosts the per-user preference groups: which optional top-level sections
 * (Admissions advisor, Career discovery) appear in the navigation; the
 * curriculum jurisdiction (state-lessons M4, flag-gated); and — UX-13 — the
 * learning preferences (grade + subjects) that onboarding collects, making
 * its "you can always change this later" promise true. Opened from the gear
 * button in the nav user area and shown only to NON-teacher users.
 *
 * DISPLAY-ONLY (the documented #56 decision): toggling a section off is a pure
 * UX preference — it hides the nav entry and route, nothing more. It is NOT a
 * consent/access signal; the COPPA + in-component consent gates remain the sole
 * source of truth server-side. We therefore never read or write this value as
 * authorization.
 *
 * CONTRACT (verify in backend/app/routes/profile.py):
 *   PATCH /api/v1/profile/preferences  body { hidden_sections: { admissions: bool, career: bool } }
 *   → BOTH booleans are REQUIRED when the key is present (a partial object 422s),
 *     so every save sends the FULL current state. Success → { "message": ... }.
 *
 * OPTIMISTIC + REVERT: a toggle updates the user state (and the localStorage
 * `user` mirror) immediately so the nav reacts instantly, then PATCHes. On
 * failure we revert to the prior value and show a WARM (amber, never red)
 * message — a failed preference save is not a system error the user caused.
 *
 * NO DEAD-END: if the user hides the section they are currently viewing, we
 * navigate them home so they're never stranded on a now-unreachable route.
 *
 * ACCESSIBILITY: a real modal dialog via the shared <Modal> primitive —
 * role="dialog" aria-modal, labelled + described, initial focus on the first
 * switch, focus TRAPPED within (Tab/Shift+Tab cycle), Escape closes, focus
 * restored to the opener on close. It hand-rolled all of that with its own
 * document keydown listener until the parent-invite a consent 403 raises OVER
 * this dialog (the state-standards picker below) turned out to close both on
 * one Escape; on the modal stack only the dialog on top acts on a key. Each
 * toggle is a native <input type="checkbox" role="switch"> so it is
 * keyboard-operable and announces on/off state to screen readers.
 *
 * WHILE A SAVE RUNS, NOTHING IN THIS FILE IS NATIVELY DISABLED (the billing
 * block the dialog lazy-loads has its own buttons). Every setting here saves
 * when it changes, so the control being saved is the one the user is on, and a
 * browser drops focus to <body> from a focused control that becomes
 * `disabled`. The dialog stays open afterwards, so they were left on <body>
 * inside a modal whether the save worked or not. Instead, every control whose
 * handler would refuse a change during a save (the one being saved, and those
 * that share its save) is aria-disabled: it keeps focus, is announced as
 * unavailable, and its handler refuses (a switch or select that is only
 * aria-disabled still changes, and React puts a controlled one back when the
 * handler leaves state alone). Style that state off [aria-disabled="true"].
 * The one native `disabled` left is the learning preferences' hydration lock,
 * which is on from the first render, before anything in it could take focus.
 */
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import axios from 'axios';
import { API_URL } from './config';
// Direct import, NOT the components/ui barrel: SettingsDialog mounts in the
// EAGER entry graph (via NavBar), and eager modules bypass the barrel so a
// future heavy re-export can't leak into the entry chunk (AgeGateModal rule).
import Button from './components/ui/Button';
import Modal from './components/ui/Modal';
import { lessonsEnabled } from './featureFlags';
import {
  CURRICULUM_JURISDICTIONS,
  updateCurriculumJurisdiction,
} from './lessonsApi';
import { isPlatformConsentRequired } from './coppa/coppaApi';
// UX-13: the SAME catalog Learning Paths renders — the subjects editor here
// structurally cannot drift from the create-path grid.
import { GRADE_CHOICES, SUBJECTS } from './subjectsCatalog';
// F14-PR3: useBilling() itself is tiny (no CSS, no heavy deps — same
// eager-safe class as useEngagementBeacon/featureFlags already imported
// above); EntitlementStatusBlock is lazy-loaded below (import/first — the
// lazy() call sits after every import, App.js's own convention).
import { useBilling } from './billing/BillingContext';
import refuseHeldEnter from './utils/refuseHeldEnter';
import './SettingsDialog.css';

// F14-PR3: EntitlementStatusBlock pulls in CheckoutFlow + Modal-adjacent UI,
// so it is lazy-loaded — and, critically, its <Suspense> boundary below is
// only ever REACHED once `billing.enabled` is confirmed true, so the chunk
// is fetched ZERO times while billing is dark.
const EntitlementStatusBlock = lazy(() =>
  import(/* webpackChunkName: "route-billing-status" */ './billing/EntitlementStatusBlock'));

// The canonical full shape the backend requires. Mirrors HIDDEN_SECTION_KEYS /
// normalize_hidden_sections server-side: any missing/absent value reads as
// "not hidden" (false) so an older `user` payload without the field is treated
// as everything-visible.
function normalizeHidden(hidden) {
  const h = hidden || {};
  return {
    admissions: h.admissions === true,
    career: h.career === true,
  };
}

// The sections this dialog governs. `routePrefix` is what we check the current
// location against so we can bounce the user home if they hide the section
// they're standing on. Driven by data so adding a future hide-able section is a
// one-line change here, not a new branch.
const SECTIONS = [
  {
    key: 'admissions',
    label: 'Show Admissions advisor',
    hint: 'College admissions guidance, shortlist, and profile.',
    routePrefix: '/advisor',
  },
  {
    key: 'career',
    label: 'Show Career discovery',
    hint: 'Career matching, insights, and assessments.',
    routePrefix: '/career',
  },
];

// onKeyDown for the two selects. A select that keeps focus keeps getting
// keys, and a closed <select> changes on each arrow or letter, which here is
// a save. Held, that is one save per option the key passes. So one press is
// one change: a key's auto-repeats are cancelled. Tab's are not (it has to
// keep moving), nor a Ctrl or Cmd chord's (a held zoom shortcut is the
// browser's, not the select's).
function onePressOneChange(e) {
  if (e.repeat && e.key !== 'Tab' && !e.ctrlKey && !e.metaKey) e.preventDefault();
}

export default function SettingsDialog({ user, onClose, onUpdateUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const firstSwitchRef = useRef(null);

  // F14-PR3: gates the lazy EntitlementStatusBlock import itself (see the
  // render below) — not just what it renders — so the chunk is never
  // fetched while billing is dark.
  const billing = useBilling();

  // Live view of the saved preference, read off the user object the parent owns.
  const hidden = normalizeHidden(user?.hidden_sections);

  // The section whose switch is being saved, if any (every switch is
  // unavailable meanwhile: handleToggle), and a single friendly error line
  // shown when a save fails.
  const [savingKey, setSavingKey] = useState(null);
  const [message, setMessage] = useState(null);

  // "My state's standards" (state-lessons M4) — the curriculum-jurisdiction
  // picker, shown ONLY when the lessons feature is on (dark launch = no new UI).
  // '' = "Not set" (maps to null on the wire → unknown / Multi-State). The value
  // is tracked locally, seeded from the user object, and reverted on a failed
  // save so the select never drifts from what the server actually holds.
  const showJurisdiction = lessonsEnabled();
  const [jurisdiction, setJurisdiction] = useState(
    user?.curriculum_jurisdiction || '',
  );
  const [jurisdictionSaving, setJurisdictionSaving] = useState(false);
  const [jurisdictionMessage, setJurisdictionMessage] = useState(null);

  // ── Learning preferences (UX-13) — the "change this later" onboarding
  // promise, finally true. Grade + subjects derive LIVE off the user object
  // (same discipline as `hidden` above): saves merge optimistically via
  // onUpdateUser and revert on failure, so there is no local mirror to drift.
  const isStudent = user?.is_student === true;
  const [prefsSaving, setPrefsSaving] = useState(false);
  const [prefsMessage, setPrefsMessage] = useState(null);

  // A user object cached BEFORE UX-13 lacks the fields (undefined, as
  // opposed to null = "student never picked"). Hydrate once from
  // GET /profile — the read surface added for exactly this — and merge.
  //
  // THE CONTROLS STAY DISABLED UNTIL HYDRATION SETTLES (Sol #549 M2): a
  // subject toggle computes "the full next array" from the CURRENT user
  // object, so an edit made before hydration lands would PATCH a baseline
  // of [] and permanently clobber the server's saved subjects — and a GET
  // landing after that edit would overwrite the optimistic state with its
  // stale snapshot. Disabling until 'idle' makes both orders impossible.
  // On failure the controls STAY disabled behind an inline retry — with the
  // network down a save couldn't succeed either, so this is not a dead end.
  const needsHydration =
    isStudent &&
    (user?.grade_level === undefined || user?.subjects === undefined);
  // 'idle' = baseline known (ready to edit) | 'loading' | 'failed'.
  const [hydration, setHydration] = useState(needsHydration ? 'loading' : 'idle');
  const hydrateCancelRef = useRef(false);

  const hydratePrefs = useCallback(async () => {
    setHydration('loading');
    try {
      const res = await axios.get(`${API_URL}/api/v1/profile`);
      if (hydrateCancelRef.current) return;
      onUpdateUser?.({
        grade_level: res?.data?.grade_level ?? null,
        subjects: res?.data?.subjects || [],
      });
      setHydration('idle');
    } catch {
      if (!hydrateCancelRef.current) setHydration('failed');
    }
  }, [onUpdateUser]);

  useEffect(() => {
    hydrateCancelRef.current = false;
    if (needsHydration) hydratePrefs();
    return () => { hydrateCancelRef.current = true; };
    // Mount-only on purpose: needsHydration is computed from the user object
    // AT OPEN TIME — the hydration merge itself flips it false, and re-runs
    // on every user change would refetch after each save.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const savePrefs = useCallback(
    async (partial, revert) => {
      setPrefsMessage(null);
      setPrefsSaving(true);
      onUpdateUser?.(partial); // optimistic — the controls re-render from user
      try {
        await axios.patch(`${API_URL}/api/v1/profile/preferences`, partial);
      } catch {
        onUpdateUser?.(revert);
        setPrefsMessage("We couldn't save that just now. Please try again.");
      } finally {
        setPrefsSaving(false);
      }
    },
    [onUpdateUser],
  );

  // Editing is only legal once the baseline is KNOWN — see hydration above.
  // This lock stays a native `disabled`: it is on from the first render (or
  // from "Try again", pressed while it is already on), so no control it locks
  // can be holding focus when it engages, and a natively disabled control
  // gets no event from the user. The two handlers refuse while it is on as
  // well, so the baseline does not rest on an attribute alone.
  const prefsHydrating = hydration !== 'idle';

  const handleGradeChange = useCallback(
    (e) => {
      // Serialize, as the chips do. The select is aria-disabled while a save
      // runs, not natively disabled, so a change still arrives here; React
      // puts the select back on the grade being saved when this returns early.
      if (prefsSaving || prefsHydrating) return;
      const raw = e.target.value;
      if (raw === '') return; // placeholder — not a choice
      const next = Number(raw);
      const prev = user?.grade_level ?? null;
      if (next === prev) return;
      savePrefs({ grade_level: next }, { grade_level: prev });
    },
    [user, prefsSaving, prefsHydrating, savePrefs],
  );

  const handleSubjectToggle = useCallback(
    (label) => {
      // Serialize — a press still arrives while saving.
      if (prefsSaving || prefsHydrating) return;
      const prev = user?.subjects || [];
      const next = prev.includes(label)
        ? prev.filter((s) => s !== label)
        : [...prev, label];
      savePrefs({ subjects: next }, { subjects: prev });
    },
    [user, prefsSaving, prefsHydrating, savePrefs],
  );

  // Toggle one section's visibility. `nextVisible` is the new "shown" state of
  // the switch; hidden is its inverse.
  const handleToggle = useCallback(
    async (section, nextVisible) => {
      // Serialize saves: each one sends the FULL object, so two in flight
      // could land out of order. The switches are aria-disabled meanwhile, not
      // natively disabled, so a flip still arrives here; React puts the
      // controlled switch back when this returns early.
      if (savingKey) return;
      setMessage(null);

      const prev = normalizeHidden(user?.hidden_sections);
      const nextHidden = { ...prev, [section.key]: !nextVisible };

      // Optimistic: update the user state + the localStorage mirror so the nav
      // (and the route guard) react instantly. The parent persists.
      onUpdateUser?.({ hidden_sections: nextHidden });

      // No dead-end: if we just HID the section the user is currently viewing,
      // send them home before the route guard would otherwise redirect them
      // mid-render. Use the router's view of the path (works under any router).
      if (
        !nextVisible &&
        location.pathname.startsWith(section.routePrefix)
      ) {
        navigate('/learn', { replace: true });
      }

      setSavingKey(section.key);
      try {
        await axios.patch(`${API_URL}/api/v1/profile/preferences`, {
          // FULL object every time — a partial is a 422 by contract.
          hidden_sections: nextHidden,
        });
      } catch (err) {
        // Revert the optimistic change and tell the user warmly. A failed
        // preference save is not the user's fault → amber, not red.
        onUpdateUser?.({ hidden_sections: prev });
        setMessage("We couldn't save that just now. Please try again.");
      } finally {
        setSavingKey(null);
      }
    },
    [savingKey, user, onUpdateUser, navigate, location],
  );

  // Save the curriculum-jurisdiction selection. '' → null (clear). On the
  // backend consent 403 the GLOBAL interceptor already raised the parent-invite
  // overlay; here we revert + show a CALM inline note in the same consent voice
  // ("ask a grown-up") — never red. Any other failure is a warm retry.
  const handleJurisdictionChange = useCallback(
    async (e) => {
      // One save at a time. The picker is aria-disabled while its save runs,
      // not natively disabled, so a change still arrives here; React puts the
      // controlled select back on the state being saved when this returns.
      if (jurisdictionSaving) return;
      const raw = e.target.value;
      const value = raw === '' ? null : raw;
      const prev = jurisdiction;
      if (raw === prev) return;

      setJurisdiction(raw); // optimistic
      setJurisdictionMessage(null);
      setJurisdictionSaving(true);
      try {
        await updateCurriculumJurisdiction(value);
        // Mirror into the user object (+ its localStorage copy) so a reopen
        // shows the saved value, matching the toggles' optimistic discipline.
        onUpdateUser?.({ curriculum_jurisdiction: value });
      } catch (err) {
        setJurisdiction(prev); // revert
        if (isPlatformConsentRequired(err)) {
          setJurisdictionMessage(
            'Ask a parent or guardian to turn this on for you first.',
          );
        } else {
          setJurisdictionMessage(
            "We couldn't save that just now. Please try again.",
          );
        }
      } finally {
        setJurisdictionSaving(false);
      }
    },
    [jurisdiction, jurisdictionSaving, onUpdateUser],
  );

  return (
    // Escape and a scrim press close (Modal's scrim rule); initial focus lands
    // on the first switch; focus returns to the gear button (the opener) on
    // close.
    <Modal
      overlayClassName="settings-overlay"
      className="settings-modal coppa-modal"
      labelledBy="settings-title"
      describedBy="settings-desc"
      onClose={() => onClose?.()}
      initialFocusRef={firstSwitchRef}
    >
      <h2 id="settings-title" className="settings-modal__title">
        Settings
      </h2>
      <p id="settings-desc" className="settings-modal__desc">
        Choose which sections appear in your navigation. You can turn them back
        on here anytime.
      </p>

      <ul className="settings-toggles">
        {SECTIONS.map((section, i) => {
          const isVisible = !hidden[section.key];
          const controlId = `settings-toggle-${section.key}`;
          const hintId = `settings-hint-${section.key}`;
          return (
            <li className="settings-toggle" key={section.key}>
              <label className="settings-toggle__label" htmlFor={controlId}>
                <span className="settings-toggle__text">
                  <span className="settings-toggle__title">{section.label}</span>
                  <span id={hintId} className="settings-toggle__hint">
                    {section.hint}
                  </span>
                </span>
                <input
                  id={controlId}
                  ref={i === 0 ? firstSwitchRef : undefined}
                  type="checkbox"
                  role="switch"
                  className="settings-switch__input"
                  checked={isVisible}
                  // Every switch while any one saves: handleToggle refuses
                  // them all, so none may look live.
                  aria-disabled={savingKey ? true : undefined}
                  aria-describedby={hintId}
                  onChange={(e) => handleToggle(section, e.target.checked)}
                />
                <span className="settings-switch" aria-hidden="true">
                  <span className="settings-switch__thumb" />
                </span>
              </label>
            </li>
          );
        })}
      </ul>

      {showJurisdiction && (
        <div className="settings-jurisdiction">
          <label
            className="settings-jurisdiction__label"
            htmlFor="settings-jurisdiction-select"
          >
            My state&rsquo;s standards
          </label>
          <p id="settings-jurisdiction-hint" className="settings-jurisdiction__hint">
            Lessons will use your state&rsquo;s standards language. Leave as
            &ldquo;Not set&rdquo; to follow Common Core.
          </p>
          <select
            id="settings-jurisdiction-select"
            className="settings-jurisdiction__select"
            value={jurisdiction}
            onChange={handleJurisdictionChange}
            onKeyDown={onePressOneChange}
            aria-disabled={jurisdictionSaving || undefined}
            aria-describedby="settings-jurisdiction-hint"
          >
            <option value="">Not set</option>
            {CURRICULUM_JURISDICTIONS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
          {jurisdictionMessage && (
            // WARM — a consent gate or a failed save is never the child's
            // fault, so this stays amber/status, never a red alert.
            <p className="settings-jurisdiction__message" role="status">
              {jurisdictionMessage}
            </p>
          )}
        </div>
      )}

      {isStudent && (
        <div className="settings-learning">
          <h3 className="settings-learning__title" id="settings-learning-title">
            Learning preferences
          </h3>
          <p className="settings-learning__hint" id="settings-learning-hint">
            Your grade and favorite subjects personalize Learning Paths.
            Change them anytime.
          </p>

          <label
            className="settings-learning__label"
            htmlFor="settings-grade-select"
          >
            My grade
          </label>
          <select
            id="settings-grade-select"
            className="settings-jurisdiction__select"
            value={user?.grade_level ?? ''}
            onChange={handleGradeChange}
            onKeyDown={onePressOneChange}
            disabled={prefsHydrating}
            aria-disabled={prefsSaving || undefined}
            aria-describedby="settings-learning-hint"
          >
            {/* Placeholder only — a set grade can be changed but not
                cleared (the backend PATCH treats null as "leave alone"). */}
            <option value="" disabled>
              Not set
            </option>
            {GRADE_CHOICES.map((g) => (
              <option key={g.value} value={g.value}>
                {g.optionLabel}
              </option>
            ))}
          </select>

          <span className="settings-learning__label" id="settings-subjects-label">
            My subjects
          </span>
          <div
            className="settings-subject-chips"
            role="group"
            aria-labelledby="settings-subjects-label"
          >
            {SUBJECTS.map((s) => {
              const active = (user?.subjects || []).includes(s.label);
              return (
                <button
                  key={s.value}
                  type="button"
                  className={`settings-subject-chip${active ? ' selected' : ''}`}
                  aria-pressed={active}
                  disabled={prefsHydrating}
                  aria-disabled={prefsSaving || undefined}
                  onClick={() => handleSubjectToggle(s.label)}
                  // The chip keeps focus through its save and comes back
                  // live, so a held Enter would flip the subject on and off,
                  // one PATCH each.
                  onKeyDown={refuseHeldEnter}
                >
                  {s.label}
                </button>
              );
            })}
          </div>

          {hydration === 'failed' && (
            // WARM retry — controls stay locked until the saved baseline
            // loads (editing over an unknown baseline could clobber it).
            <p className="settings-jurisdiction__message" role="status">
              We couldn&rsquo;t load your saved preferences.{' '}
              <button
                type="button"
                className="settings-subject-chip"
                onClick={hydratePrefs}
              >
                Try again
              </button>
            </p>
          )}

          {prefsMessage && (
            // WARM — a failed save is never the student's fault.
            <p className="settings-jurisdiction__message" role="status">
              {prefsMessage}
            </p>
          )}
        </div>
      )}

      {/* F14-PR3: "Your plan" — status + upgrade/manage actions. The
          billing.enabled check here gates the LAZY IMPORT itself (see the
          import above), not just the rendered output, so a dark deploy
          never fetches this chunk from the Settings dialog. */}
      {billing.enabled && (
        <Suspense fallback={null}>
          <EntitlementStatusBlock />
        </Suspense>
      )}

      {message && (
        // WARM (amber) — a failed save is not a system error the user caused.
        <p className="settings-message" role="status">
          {message}
        </p>
      )}

      <div className="settings-modal__actions">
        <Button
          variant="primary"
          onClick={() => onClose?.()}
        >
          Done
        </Button>
      </div>
    </Modal>
  );
}
